import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Input, Switch, Tag, Tooltip, message, Button, Modal, Drawer, Form, Select, InputNumber, Space, Popconfirm,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined, HeartOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import {
  getGateways, updateGateway, checkGatewaysHealth,
  getGatewayAccounts, createGatewayAccount, updateGatewayAccount, deleteGatewayAccount,
} from '../../../services/logistics';
import type { Gateway, GatewayAccount } from '../../../services/logistics';

const PAGE_SIZE = 20;

const HEALTH_COLOR: Record<string, string> = {
  healthy: 'green',
  degraded: 'orange',
  down: 'red',
  unknown: 'default',
};

const markupText = (g: Gateway) => {
  if (g.markup_type === 'percentage') return `按比例加价 ${g.markup_value ?? '-'}%`;
  if (g.markup_type === 'fixed') return `固定加价 ${g.markup_value ?? '-'}`;
  return '不加价';
};

// 不同网关的凭证 JSON 结构不一样（照抄各自 Adapter 里 credentials 的读法），这里只给个格式提示，
// 不做成一个个独立输入框——网关一多，维护成本比让运营直接编辑 JSON 还高。
const CREDENTIAL_HINTS: Record<string, string> = {
  BBL: '{\n  "app_id": "your_app_id",\n  "secret_key": "your_secret_key",\n  "base_url": "https://..."\n}',
  EASYPOST: '{\n  "api_key": "your_api_key"\n}',
  SHIPPO: '{\n  "api_key": "your_api_key"\n}',
  EZEESHIP: '{\n  "api_key": "your_api_key",\n  "base_url": "https://ezeeship.com/api/ezeeship-openapi",\n  "service_codes": [["fedex", "fedex_ground"], ["usps", "usps_priority"]]\n}',
};
const getCredentialHint = (code: string) => CREDENTIAL_HINTS[code] || '{\n  "api_key": "your_api_key"\n}';

// ======================== 网关账号（凭证）子表 ========================

const GatewayAccountSubTable: React.FC<{ gateway: Gateway }> = ({ gateway }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GatewayAccount[]>([]);
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const [editing, setEditing] = useState<Partial<GatewayAccount> | null>(null);
  const [credentialsText, setCredentialsText] = useState('');
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getGatewayAccounts({ page_size: 500, gateway: gateway.id });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
    } catch {
      message.error('获取网关账号失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [gateway.id]);

  const patchLocalRow = (id: number, updated: Partial<GatewayAccount>) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...updated } : row)));
  };

  const handleToggleActive = async (record: GatewayAccount, checked: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(record.id));
    try {
      const resp = await updateGatewayAccount(record.id, { is_active: checked });
      patchLocalRow(record.id, resp.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '更新失败');
    } finally {
      setTogglingIds((prev) => { const next = new Set(prev); next.delete(record.id); return next; });
    }
  };

  const openCreate = () => {
    setEditing({});
    form.resetFields();
    form.setFieldsValue({ environment: 'sandbox', is_active: true });
    setCredentialsText('');
  };

  const openEdit = (record: GatewayAccount) => {
    setEditing(record);
    form.setFieldsValue(record);
    // 凭证不回显明文，编辑框留空表示"不修改"，不能预填任何内容
    setCredentialsText('');
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteGatewayAccount(id);
      message.success('已删除');
      fetchData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const isCreate = !editing?.id;

      let credentials: Record<string, unknown> | undefined;
      const trimmed = credentialsText.trim();
      if (trimmed) {
        try {
          credentials = JSON.parse(trimmed);
        } catch {
          message.error('凭证不是合法的 JSON 格式');
          return;
        }
        if (typeof credentials !== 'object' || Array.isArray(credentials) || credentials === null) {
          message.error('凭证必须是一个 JSON 对象，如 {"api_key": "..."}');
          return;
        }
      } else if (isCreate) {
        message.error('新增账号必须填写凭证');
        return;
      }

      setSaving(true);
      const payload = {
        gateway: gateway.id,
        name: values.name,
        environment: values.environment,
        external_account_ref: values.external_account_ref || '',
        balance: values.balance ?? null,
        is_active: values.is_active,
        ...(credentials ? { credentials } : {}),
      };
      if (isCreate) {
        await createGatewayAccount(payload);
      } else {
        await updateGatewayAccount(editing!.id!, payload);
      }
      message.success('保存成功');
      setEditing(null);
      fetchData();
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data;
      const firstError = detail && typeof detail === 'object' ? Object.values(detail)[0] : null;
      message.error((Array.isArray(firstError) ? firstError[0] : firstError) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<GatewayAccount> = [
    { title: '账号名称', dataIndex: 'name', width: 160 },
    {
      title: '环境',
      dataIndex: 'environment_display',
      width: 90,
      render: (text: string, r: GatewayAccount) => <Tag color={r.environment === 'production' ? 'red' : 'blue'}>{text}</Tag>,
    },
    { title: '外部账号/合同号', dataIndex: 'external_account_ref', width: 160, render: (v?: string) => v || '-' },
    { title: '余额', dataIndex: 'balance', width: 100, render: (v?: string) => v ?? '-' },
    {
      title: '凭证状态',
      dataIndex: 'has_credentials',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">已配置</Tag> : <Tag color="orange">未配置</Tag>),
    },
    {
      title: '启用',
      key: 'is_active',
      width: 80,
      render: (_: any, r: GatewayAccount) => (
        <Switch checked={r.is_active} disabled={togglingIds.has(r.id)} onChange={(c) => handleToggleActive(r, c)} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      render: (_: any, r: GatewayAccount) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除这个网关账号？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button type="dashed" icon={<PlusOutlined />} onClick={openCreate}>新增账号</Button>
      </div>
      <Table<GatewayAccount>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 700 }}
        locale={{ emptyText: '该网关下暂无账号' }}
      />

      <Modal
        title={editing?.id ? `编辑网关账号：${editing.name}` : `新增网关账号（${gateway.name}）`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="账号名称" rules={[{ required: true, message: '请填写账号名称' }]}>
            <Input placeholder="如 sandbox-test、生产账号" />
          </Form.Item>
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item name="environment" label="环境" rules={[{ required: true, message: '请选择环境' }]} style={{ width: 200 }}>
              <Select
                options={[
                  { value: 'sandbox', label: '测试环境' },
                  { value: 'production', label: '生产环境' },
                ]}
              />
            </Form.Item>
            <Form.Item name="is_active" label="启用" valuePropName="checked"><Switch /></Form.Item>
          </Space>
          <Form.Item name="external_account_ref" label="外部账号/合同号（选填）">
            <Input />
          </Form.Item>
          <Form.Item name="balance" label="账户余额（选填，适用于有预充值机制的网关）">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item
            label={editing?.id ? '凭证（留空表示不修改）' : '凭证（JSON 格式，必填）'}
            required={!editing?.id}
          >
            <Input.TextArea
              rows={6}
              value={credentialsText}
              onChange={(e) => setCredentialsText(e.target.value)}
              placeholder={getCredentialHint(gateway.code)}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ======================== 网关主表 ========================

const GatewayTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Gateway[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [hideDisabled, setHideDisabled] = useState(true);
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const [editing, setEditing] = useState<Gateway | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const markupType = Form.useWatch('markup_type', form);

  // 管理网关账号用 Drawer 展示，而不是表格行内展开——避免表格套表格的视觉混乱
  const [managingGateway, setManagingGateway] = useState<Gateway | null>(null);

  const fetchData = async (p: number, q?: string, ps?: number, hideDisabledParam?: boolean) => {
    const size = ps || pageSize;
    const hide = hideDisabledParam ?? hideDisabled;
    setLoading(true);
    try {
      const resp = await getGateways({
        page: p, page_size: size, search: q || undefined,
        is_active: hide ? true : undefined,
      });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
      setTotal(Number(body?.count ?? 0) || 0);
      setPage(p);
      setPageSize(size);
    } catch {
      message.error('获取网关列表失败');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(1, ''); }, []);

  const handleSearch = () => fetchData(1, searchText);

  const handleToggleHideDisabled = (checked: boolean) => {
    setHideDisabled(checked);
    fetchData(1, searchText, undefined, checked);
  };

  const [checkingHealth, setCheckingHealth] = useState(false);

  const handleCheckHealth = async () => {
    setCheckingHealth(true);
    try {
      const resp = await checkGatewaysHealth();
      const results = resp.data.results || [];
      const healthyCount = results.filter((r) => !r.skipped && r.health_status === 'healthy').length;
      const downCount = results.filter((r) => !r.skipped && r.health_status !== 'healthy').length;
      const skippedCount = results.filter((r) => r.skipped).length;
      const parts = [`正常 ${healthyCount} 个`];
      if (downCount > 0) parts.push(`异常 ${downCount} 个`);
      if (skippedCount > 0) parts.push(`未检测（无启用账号）${skippedCount} 个`);
      message.success(`检测完成：${parts.join('，')}`);
      fetchData(page, searchText);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '检测失败');
    } finally {
      setCheckingHealth(false);
    }
  };

  const patchLocalRow = (id: number, updated: Partial<Gateway>) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...updated } : row)));
  };

  const handleToggleActive = async (record: Gateway, checked: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(record.id));
    try {
      const resp = await updateGateway(record.id, { is_active: checked });
      patchLocalRow(record.id, resp.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '更新失败');
    } finally {
      setTogglingIds((prev) => { const next = new Set(prev); next.delete(record.id); return next; });
    }
  };

  const openEdit = (record: Gateway) => {
    setEditing(record);
    form.setFieldsValue({
      markup_type: record.markup_type,
      markup_value: record.markup_value ? Number(record.markup_value) : undefined,
      supports_signature: record.supports_signature,
      supports_insurance: record.supports_insurance,
      remarks: record.remarks,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const resp = await updateGateway(editing.id, {
        markup_type: values.markup_type,
        markup_value: values.markup_type === 'none' ? null : values.markup_value,
        supports_signature: values.supports_signature,
        supports_insurance: values.supports_insurance,
        remarks: values.remarks,
      });
      patchLocalRow(editing.id, resp.data);
      message.success('保存成功');
      setEditing(null);
    } catch (e: any) {
      if (e?.errorFields) return; // 表单自身校验未通过
      const detail = e?.response?.data;
      const msg = detail?.markup_value?.[0] || detail?.detail || '保存失败';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Gateway> = useMemo(() => ([
    { title: '网关代码', dataIndex: 'code', width: 140 },
    { title: '网关名称', dataIndex: 'name', width: 180 },
    { title: '类型', dataIndex: 'gateway_type_display', width: 120 },
    {
      title: '启用',
      key: 'is_active',
      width: 80,
      render: (_: any, r: Gateway) => (
        <Switch checked={r.is_active} disabled={togglingIds.has(r.id)} onChange={(c) => handleToggleActive(r, c)} />
      ),
    },
    {
      title: '健康状态',
      key: 'health_status',
      width: 100,
      render: (_: any, r: Gateway) => <Tag color={HEALTH_COLOR[r.health_status] || 'default'}>{r.health_status_display}</Tag>,
    },
    { title: '关联账号数', dataIndex: 'account_count', width: 100 },
    { title: '加价策略', key: 'markup', width: 160, render: (_: any, r: Gateway) => markupText(r) },
    {
      title: '备注',
      dataIndex: 'remarks',
      width: 200,
      ellipsis: true,
      render: (text?: string) => text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 190,
      fixed: 'right',
      render: (_: any, r: Gateway) => (
        <Space>
          <Button type="link" icon={<UnorderedListOutlined />} onClick={() => setManagingGateway(r)}>管理账号</Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
        </Space>
      ),
    },
  ]), [togglingIds]);

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#999' }}>
        网关是物流产品的提供方（外部聚合平台，或未来自营渠道）。新增网关需要先开发对应的对接代码；
        点击"管理账号"可以管理这个网关下的账号（API Key 等凭证），点击"编辑"可设置网关的默认加价策略。
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="搜索网关名称/代码"
          prefix={<SearchOutlined />}
          style={{ width: 240 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
        <Button onClick={handleSearch}>搜索</Button>
        <Button icon={<HeartOutlined />} loading={checkingHealth} onClick={handleCheckHealth}>检测网关状态</Button>
        <Space size={6}>
          <Switch checked={hideDisabled} onChange={handleToggleHideDisabled} />
          <span>隐藏已禁用网关</span>
        </Space>
      </div>

      <Table<Gateway>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onChange={(p) => fetchData(Number(p.current || 1), searchText, Number(p.pageSize || pageSize))}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`,
        } as TablePaginationConfig}
        scroll={{ x: 1300 }}
      />

      <Modal
        title={editing ? `编辑网关：${editing.name}` : ''}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="markup_type" label="加价方式" rules={[{ required: true, message: '请选择加价方式' }]}>
            <Select
              options={[
                { value: 'none', label: '不加价' },
                { value: 'percentage', label: '按比例加价' },
                { value: 'fixed', label: '固定金额加价' },
              ]}
            />
          </Form.Item>
          {markupType && markupType !== 'none' && (
            <Form.Item
              name="markup_value"
              label={markupType === 'percentage' ? '加价百分比（如 12.5 表示加价 12.5%，填 -10 表示降价 10% 做优惠）' : '加价金额（可填负数做降价优惠，如 -0.05）'}
              rules={[{ required: true, message: '请填写加价数值' }]}
            >
              <InputNumber style={{ width: '100%' }} precision={2} />
            </Form.Item>
          )}
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item
              name="supports_signature"
              label="支持签名服务"
              valuePropName="checked"
              tooltip="这个网关的 API 对接代码是否真的能把签名服务要求传给承运商并体现在报价里——不是承运商产品本身支不支持，是我们这套集成代码技术上能不能做到。关闭后，客户询价要求签名服务时会跳过这个网关，避免返回没有真正包含签名服务的报价。"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="supports_insurance"
              label="支持保险"
              valuePropName="checked"
              tooltip="这个网关的 API 对接代码是否真的能把保险要求传给承运商并体现在报价里。关闭后，客户询价要求保险时会跳过这个网关，避免返回没有真正包含保险的报价。"
            >
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="remarks" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={managingGateway ? `账号管理：${managingGateway.name}（${managingGateway.code}）` : ''}
        open={!!managingGateway}
        onClose={() => setManagingGateway(null)}
        width={860}
        destroyOnClose
      >
        {managingGateway && <GatewayAccountSubTable gateway={managingGateway} />}
      </Drawer>
    </div>
  );
};

export default GatewayTab;
